import { useEffect, useRef } from "react";
import { ArrowLeft, X, CalendarDays } from "lucide-react";
import { Dropdown } from "./Dropdown";

/**
 * Canonical popup size used across the entire codebase.
 * Width: max-w-2xl (672px) — comfortable for forms, confirmations, and pickers.
 * Height: capped at 85vh so the popup never touches the viewport edges.
 *
 * The `size` prop is intentionally ignored — every popup in the app uses
 * the same dimensions for visual consistency. The prop is kept on the
 * interface only for backwards compatibility with existing call sites.
 */
const POPUP_WIDTH_CLASS = "max-w-2xl";
const POPUP_WIDE_WIDTH_CLASS = "max-w-5xl";
const POPUP_HEIGHT_CLASS = "max-h-[85vh]";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: React.ReactNode;
  /** Deprecated — every modal renders at the same canonical size. */
  size?: "sm" | "md" | "lg" | "xl" | "2xl";
  /** Opt-in wider width for multi-column wizards. Regular modals should NOT set this. */
  wide?: boolean;
  footer?: React.ReactNode;
  closable?: boolean;
  /** When provided, renders a back arrow in the header on the left of the title. */
  onBack?: () => void;
}

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  closable = true,
  onBack,
  wide = false,
}: ModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && closable) onClose();
    };
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose, closable]);

  if (!open) return null;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={(e) => e.target === overlayRef.current && closable && onClose()}
    >
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" />

      <div
        className={`relative w-full ${wide ? POPUP_WIDE_WIDTH_CLASS : POPUP_WIDTH_CLASS} rounded-xl border border-border bg-card shadow-xl flex flex-col ${POPUP_HEIGHT_CLASS}`}
      >
        <div
          className={`flex justify-between gap-4 p-5 border-b border-border flex-shrink-0 ${
            description ? "items-start" : "items-center"
          }`}
        >
          <div
            className={`flex gap-2.5 min-w-0 ${
              description ? "items-start" : "items-center"
            }`}
          >
            {onBack && (
              <button
                onClick={onBack}
                className="flex-shrink-0 p-1.5 -ml-1.5 rounded-md d hover:text-foreground hover:bg-accent transition-colors"
                aria-label="Back"
              >
                <ArrowLeft className="size-4" />
              </button>
            )}
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-foreground leading-tight text-start">
                {title}
              </h2>
              {description && (
                <p className="text-sm d mt-2 text-start">{description}</p>
              )}
            </div>
          </div>
          {closable && (
            <button
              onClick={onClose}
              className="flex-shrink-0 p-1.5 -mr-1.5 rounded-md d hover:text-foreground hover:bg-accent transition-colors"
              aria-label="Close"
            >
              <X className="size-4" />
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">{children}</div>

        {footer && (
          <div className="flex items-center justify-end gap-3 p-5 border-t border-border flex-shrink-0 bg-card">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * `ModalShell` is the same outer wrapper as `Modal` (backdrop + canonical
 * size + frame), but renders no header/footer chrome — you supply your
 * own internal layout via `children`. Use this when you need bespoke
 * header controls (back button, status chips, multiple action buttons)
 * but still want the standard popup size and backdrop.
 *
 * For 95% of popups, prefer `Modal` instead.
 */
export function ModalShell({
  open,
  onClose,
  children,
  closable = true,
  zIndexClass = "z-50",
  variant = "default",
}: {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  closable?: boolean;
  zIndexClass?: string;
  variant?: "default" | "preview" | "wide";
}) {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && closable) onClose();
    };
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose, closable]);

  if (!open) return null;

  const panelSizeClass =
    variant === "preview"
      ? "max-w-6xl h-[90vh] max-h-[90vh]"
      : variant === "wide"
        ? "max-w-5xl max-h-[88vh]"
        : `${POPUP_WIDTH_CLASS} ${POPUP_HEIGHT_CLASS}`;

  return (
    <div
      ref={overlayRef}
      className={`fixed inset-0 ${zIndexClass} flex items-center justify-center p-4`}
      onClick={(e) => e.target === overlayRef.current && closable && onClose()}
    >
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" />
      <div
        className={`relative w-full ${panelSizeClass} rounded-xl border border-border bg-card shadow-xl flex flex-col overflow-hidden`}
      >
        {children}
      </div>
    </div>
  );
}

/* ── Shared form primitives used across modals ── */

export function FormField({
  label,
  required,
  children,
  hint,
  error,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
  hint?: string;
  error?: string;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-foreground">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
      {error && <p className="text-xs text-red-500">{error}</p>}
      {hint && !error && <p className="text-xs d">{hint}</p>}
    </div>
  );
}

export function FormInput({
  placeholder,
  defaultValue,
  value,
  onChange,
  type = "text",
  disabled,
  autoComplete,
  name,
  maxLength,
  min,
  max,
}: {
  placeholder?: string;
  defaultValue?: string;
  value?: string;
  onChange?: (v: string) => void;
  type?: string;
  disabled?: boolean;
  autoComplete?: string;
  name?: string;
  maxLength?: number;
  min?: number;
  max?: number;
}) {
  const isDate = type === "date";
  return (
    <input
      type={type}
      name={name}
      autoComplete={autoComplete}
      lang={isDate ? "en-GB" : undefined}
      placeholder={placeholder}
      defaultValue={defaultValue}
      value={value}
      onChange={onChange ? (e) => onChange(e.target.value) : undefined}
      disabled={disabled}
      maxLength={maxLength}
      min={min}
      max={max}
      onClick={
        isDate
          ? (e) => {
              const el = e.currentTarget as HTMLInputElement & {
                showPicker?: () => void;
              };
              el.showPicker?.();
            }
          : undefined
      }
      style={isDate ? { colorScheme: "light dark" } : undefined}
      className={`w-full text-sm bg-[#f5f7fb] dark:bg-white/[0.04] rounded-lg px-3 py-2 outline-none border border-[#e2e8f0] dark:border-white/[0.07] focus:border-indigo-500 dark:focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 dark:focus:ring-indigo-500/10 transition-all placeholder:text-[#64748b] dark:placeholder:text-white/25 text-[#0f172a] dark:text-white disabled:opacity-50 ${isDate ? "cursor-pointer [&::-webkit-datetime-edit-fields-wrapper]:bg-transparent [&::-webkit-datetime-edit]:bg-transparent [&::selection]:bg-transparent" : ""}`}
    />
  );
}

export function FormSelect({
  options,
  value,
  onChange,
  placeholder,
}: {
  options: (string | { value: string; label: string })[];
  value?: string;
  onChange?: (v: string) => void;
  placeholder?: string;
}) {
  const normalized = options.map((o) =>
    typeof o === "string" ? { value: o, label: o } : o,
  );
  return (
    <Dropdown
      value={value ?? ""}
      onChange={onChange ?? (() => {})}
      options={normalized}
      placeholder={placeholder}
      variant="form"
    />
  );
}

export function FormTextarea({
  placeholder,
  value,
  onChange,
  rows = 3,
  maxRows = 8,
}: {
  placeholder?: string;
  value?: string;
  onChange?: (v: string) => void;
  rows?: number;
  maxRows?: number;
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight || "20") || 20;
    const maxHeight = lineHeight * maxRows;
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
  }, [value, maxRows]);

  return (
    <textarea
      ref={textareaRef}
      placeholder={placeholder}
      value={value}
      onChange={onChange ? (e) => onChange(e.target.value) : undefined}
      rows={rows}
      className="w-full text-sm bg-[#f5f7fb] dark:bg-white/[0.04] rounded-lg px-3 py-2 outline-none border border-[#e2e8f0] dark:border-white/[0.07] focus:border-indigo-500 dark:focus:border-indigo-500 transition-all placeholder:text-[#64748b] dark:placeholder:text-white/25 resize-none leading-relaxed text-[#0f172a] dark:text-white overflow-y-auto"
    />
  );
}

/**
 * Native date picker styled to show DD/MM/YYYY.
 * `value` and `onChange` use YYYY-MM-DD (ISO) format internally.
 */
export function FormDatePicker({
  value,
  onChange,
  placeholder = "DD/MM/YYYY",
  min,
}: {
  value?: string;
  onChange?: (v: string) => void;
  placeholder?: string;
  min?: string;
}) {
  const display = value
    ? new Date(value + "T00:00:00").toLocaleDateString("en-GB")
    : placeholder;

  return (
    <div
      className="relative cursor-pointer"
      onClick={(e) => {
        const input = (e.currentTarget as HTMLElement).querySelector("input");
        if (input) {
          try {
            input.showPicker();
          } catch {
            input.focus();
          }
        }
      }}
    >
      <input
        type="date"
        value={value ?? ""}
        onChange={onChange ? (e) => onChange(e.target.value) : undefined}
        min={min}
        className="w-full px-3 py-2 text-sm bg-[#f5f7fb] dark:bg-white/[0.04] border border-[#e2e8f0] dark:border-white/[0.07] rounded-lg outline-none focus:border-indigo-500 dark:focus:border-indigo-500 transition-all text-transparent dark:text-transparent cursor-pointer [&::-webkit-datetime-edit]:text-transparent [&::-webkit-datetime-edit-fields-wrapper]:text-transparent [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
      />
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm pointer-events-none text-[#0f172a] dark:text-white">
        {display}
      </span>
      <CalendarDays className="absolute right-3 top-1/2 -translate-y-1/2 size-4 d pointer-events-none" />
    </div>
  );
}

export function ModalFooterButtons({
  onCancel,
  onSubmit,
  submitLabel = "Save",
  submitDisabled,
}: {
  onCancel: () => void;
  onSubmit: () => void;
  submitLabel?: string;
  submitDisabled?: boolean;
}) {
  return (
    <div className="flex w-full items-center justify-end gap-3">
      <button
        onClick={onCancel}
        className="px-4 py-2 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-sm font-medium text-[#475569] dark:text-white/60 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] transition-colors"
      >
        Cancel
      </button>
      <button
        onClick={onSubmit}
        disabled={submitDisabled}
        className="px-4 py-2 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors disabled:opacity-40 shadow-md shadow-indigo-500/20 dark:shadow-indigo-500/20"
      >
        {submitLabel}
      </button>
    </div>
  );
}
